import {Button, Input, Stack, HStack, Field, Text} from "@chakra-ui/react";
import {useMemo, useState} from "react";
import api from "../../api";
import {RegisteredUser, RoleId, TeamId} from "platformed-client/types";
import {useValidation, useValidatedPromiseState} from "../../hooks/validationState";
import {withDialog} from "../../state/withDialog";
import {DialogBody, DialogFooter, DialogHeader, DialogTitle} from "../../components/ui/dialog";
import {Alert} from "../../components/ui/alert";
import {q, useQueryData, useQueriesData} from "../../state";
import {ChipMultiSelect} from "../../components/ChipMultiSelect";
import {roleColor, roleLabel} from "../../components/roleDisplay";
import {useVisibleRoles} from "../../hooks/accessibleProducts";
import T from "platformed-components/i18n/T";
import useTranslate from "platformed-components/i18n/useTranslate";

const SectionLabel = ({children}: {children: React.ReactNode}) => (
  <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide">
    {children}
  </Text>
);

export const InviteUserDialog = withDialog(
  ({
    initialName,
    initialEmail,
    onClose,
    onInvite,
  }: {
    initialName?: string;
    initialEmail?: string;
    onClose: () => void;
    onInvite?: (user: RegisteredUser) => Promise<unknown>;
  }) => {
    const t = useTranslate();
    const roles = useVisibleRoles();
    const teams = useQueryData(q.teams());

    const [name, setName] = useState(initialName ?? "");
    const [email, setEmail] = useState(initialEmail ?? "");
    const [roleIds, setRoleIds] = useState<RoleId[]>([]);
    const [teamIds, setTeamIds] = useState<TeamId[]>([]);

    const roleOptions = useMemo(() => roles.map(r => ({label: roleLabel(r, t), value: r.role_id})), [roles, t]);
    const roleColorById = useMemo(() => new Map(roles.map(r => [r.role_id as string, roleColor(r)])), [roles]);
    const teamOptions = useMemo(() => teams.map(team => ({label: team.name, value: team.team_id})), [teams]);

    // A user gets access via a role granted directly or via a team's roles. Prefetch
    // every team's roles (stable list, so this only suspends once on open) so we can
    // warn when an invite would grant no access at all.
    const teamPermissions = useQueriesData({queries: teams.map(team => q.teamPermissions(team.team_id))});
    const teamsWithAccess = useMemo(
      () => new Set(teams.filter((_, i) => teamPermissions[i].length > 0).map(team => team.team_id)),
      [teams, teamPermissions],
    );
    const willHaveNoAccess = roleIds.length === 0 && !teamIds.some(id => teamsWithAccess.has(id));

    const [validationErrors, setValidationErrors] = useValidation(useState({}), {name, email});
    const [inviting, invite] = useValidatedPromiseState(
      async () => {
        const user = await api.users.invite({name, email, role_ids: roleIds, team_ids: teamIds});
        onInvite && (await onInvite(user));
        onClose();
      },
      [name, email, roleIds, teamIds, onClose, onInvite],
      setValidationErrors,
    );

    return (
      <form onSubmit={invite}>
        <DialogHeader>
          <DialogTitle>
            <T keyName="c.Invite_new_user" />
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Stack gap="6" mb="4">
            <Stack gap="4">
              <Field.Root required>
                <Field.Label>
                  <T keyName="c.Name" />
                </Field.Label>
                <Input onChange={e => setName(e.target.value)} value={name} placeholder={t("c.Jane_Smith")} autoFocus />
              </Field.Root>
              <Field.Root required invalid={validationErrors.email !== undefined}>
                <Field.Label>
                  <T keyName="c.Email" />
                </Field.Label>
                <Input onChange={e => setEmail(e.target.value)} value={email} placeholder="jane@example.com" />
                {validationErrors.email && validationErrors.email[0] && (
                  <Field.ErrorText>{validationErrors.email[0].message}</Field.ErrorText>
                )}
              </Field.Root>
            </Stack>

            <Stack gap="4" bg="gray.50" mx="-6" px="6" py="4" borderYWidth="1px" borderColor="gray.200">
              <SectionLabel>
                <T keyName="c.Access" />
              </SectionLabel>
              <Field.Root>
                <Field.Label>
                  <T keyName="c.Roles" />
                </Field.Label>
                <ChipMultiSelect
                  options={roleOptions}
                  value={roleIds}
                  onValueChange={value => setRoleIds(value as RoleId[])}
                  chipColor={id => roleColorById.get(id) ?? "gray"}
                  placeholder={t("c.No_roles")}
                  size="md"
                />
              </Field.Root>
              <Field.Root>
                <Field.Label>
                  <T keyName="c.Teams" />
                </Field.Label>
                <ChipMultiSelect
                  options={teamOptions}
                  value={teamIds}
                  onValueChange={value => setTeamIds(value as TeamId[])}
                  chipColor={() => "gray"}
                  placeholder={t("c.No_teams")}
                  size="md"
                />
              </Field.Root>
              {willHaveNoAccess ? (
                <Alert status="warning" title={<T keyName="app.dialogs.invite_user.no_access_warning" />} />
              ) : null}
            </Stack>

            {inviting.lastError ? (
              <Alert status="error" title={<T keyName="c.Error_inviting_user" />}>
                {`${inviting.lastError}`}
              </Alert>
            ) : null}
          </Stack>
        </DialogBody>

        <DialogFooter>
          <HStack gap="3">
            <Button variant="ghost" onClick={onClose} disabled={inviting.inProgress}>
              <T keyName="c.Cancel" />
            </Button>
            <Button colorPalette="blue" type="submit" loading={inviting.inProgress}>
              <T keyName="c.Send_invitation" />
            </Button>
          </HStack>
        </DialogFooter>
      </form>
    );
  },
);
